import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../trpc.js';
import {
  Alerta,
  Cartao,
  Carregando,
  ErroDeCarga,
  Etiqueta,
  Tabela,
  Vazio,
  formatarNumero,
} from '../comum/ui.js';

/**
 * FILA DE APROVAÇÃO (§7).
 *
 * O ponto desta tela: aprovar em lote SEM ver o diff é o mesmo que não
 * aprovar. Por isso o diff por colaborador é obrigatório na interface — e as
 * linhas com variação alta, novas e removidas vêm destacadas primeiro.
 */
export function FilaAprovacao({ competenciaAnterior }: { competenciaAnterior: string }) {
  const cliente = useQueryClient();
  const [selecionadas, setSelecionadas] = useState<Set<string>>(new Set());
  const [expandida, setExpandida] = useState<string | null>(null);

  const fila = useQuery({
    queryKey: ['staging'],
    queryFn: () => api.staging.listar.query({}),
  });

  const aprovar = useMutation({
    mutationFn: (ids: string[]) => api.staging.aprovarEmLote.mutate({ stagingIds: ids }),
    onSuccess: () => {
      setSelecionadas(new Set());
      void cliente.invalidateQueries({ queryKey: ['staging'] });
    },
  });

  if (fila.isLoading) return <Carregando o="fila de aprovação" />;
  if (fila.error) return <ErroDeCarga erro={fila.error} />;

  const entradas = fila.data ?? [];
  if (entradas.length === 0) {
    return <Vazio mensagem="Nada aguardando aprovação." sugestao="As entradas da API aparecem aqui." />;
  }

  const alternar = (id: string) => {
    const proxima = new Set(selecionadas);
    if (proxima.has(id)) proxima.delete(id);
    else proxima.add(id);
    setSelecionadas(proxima);
  };

  const bloqueadas = entradas.filter((e) => e.status === 'PENDENTE_MAPEAMENTO');

  return (
    <div className="tela">
      <h1>Fila de aprovação</h1>

      {bloqueadas.length > 0 ? (
        <Alerta severidade="ATENCAO" titulo={`${bloqueadas.length} entrada(s) com código sem De-Para`}>
          Nada foi descartado. Cadastre o mapeamento em <strong>Cadastros → De-Para do ponto</strong> e a
          entrada volta para a fila.
        </Alerta>
      ) : null}

      <Cartao
        titulo={`${entradas.length} entrada(s)`}
        acao={
          <button
            type="button"
            className="botao botao--primario"
            disabled={selecionadas.size === 0 || aprovar.isPending}
            onClick={() => aprovar.mutate([...selecionadas])}
          >
            {aprovar.isPending ? 'Aprovando…' : `Aprovar ${selecionadas.size} selecionada(s)`}
          </button>
        }
      >
        {aprovar.error ? <ErroDeCarga erro={aprovar.error} /> : null}

        <Tabela cabecalhos={['', 'Tipo', 'Origem', 'Competência', '#Itens', 'Status', 'Recebida em', '']}>
          {entradas.map((entrada) => {
            const bloqueada = entrada.status === 'PENDENTE_MAPEAMENTO';
            return (
              <tr key={entrada.id} className={bloqueada ? 'linha--bloqueada' : undefined}>
                <td>
                  <input
                    type="checkbox"
                    checked={selecionadas.has(entrada.id)}
                    disabled={bloqueada}
                    onChange={() => alternar(entrada.id)}
                    aria-label={`Selecionar entrada ${entrada.id}`}
                  />
                </td>
                <td>{entrada.tipo}</td>
                <td>
                  {entrada.parceiro ?? entrada.origem}
                  <span className="sutil"> · {entrada.origem}</span>
                </td>
                <td>{entrada.competencia ?? '—'}</td>
                <td className="numerica">{entrada.quantidadeItens}</td>
                <td>
                  <Etiqueta
                    texto={entrada.status.replace(/_/g, ' ')}
                    tom={bloqueada ? 'atencao' : 'neutro'}
                  />
                </td>
                <td className="sutil">{new Date(entrada.criadoEm).toLocaleString('pt-BR')}</td>
                <td>
                  <button
                    type="button"
                    className="botao botao--texto"
                    onClick={() => setExpandida(expandida === entrada.id ? null : entrada.id)}
                  >
                    {expandida === entrada.id ? 'Fechar diff' : 'Ver diff'}
                  </button>
                </td>
              </tr>
            );
          })}
        </Tabela>
      </Cartao>

      {expandida ? <Diff stagingId={expandida} competenciaAnterior={competenciaAnterior} /> : null}
    </div>
  );
}

function Diff({
  stagingId,
  competenciaAnterior,
}: {
  stagingId: string;
  competenciaAnterior: string;
}) {
  const consulta = useQuery({
    queryKey: ['staging-diff', stagingId, competenciaAnterior],
    queryFn: () => api.staging.diff.query({ stagingId, competenciaAnterior }),
  });

  if (consulta.isLoading) return <Carregando o="diff" />;
  if (consulta.error) return <ErroDeCarga erro={consulta.error} />;

  const dados = consulta.data;
  if (!dados) return null;

  // Ordem deliberada: o que mudou muito primeiro; o "normal" por último.
  const peso = { VARIACAO_ALTA: 0, NOVO: 1, REMOVIDO: 2, NORMAL: 3 } as const;
  const linhas = [...dados.linhas].sort((a, b) => peso[a.destaque] - peso[b.destaque]);

  return (
    <Cartao
      titulo={`Diff contra ${competenciaAnterior}`}
      acao={
        <span className="sutil">
          {dados.resumo.variacaoAlta} variação alta · {dados.resumo.novos} novo(s) ·{' '}
          {dados.resumo.removidos} removido(s)
        </span>
      }
    >
      <Tabela cabecalhos={['Colaborador', 'Rubrica', '#Anterior', '#Atual', '#Variação', '']}>
        {linhas.map((linha, i) => (
          <tr key={`${linha.matricula}-${linha.rubricaCodigo}-${i}`}>
            <td>{linha.matricula ?? '—'}</td>
            <td>{linha.rubricaCodigo}</td>
            <td className="numerica">{formatarNumero(linha.anterior)}</td>
            <td className="numerica">{formatarNumero(linha.atual)}</td>
            <td className="numerica">
              {linha.variacaoPct === null ? '—' : `${formatarNumero(linha.variacaoPct)}%`}
            </td>
            <td>
              {linha.destaque === 'NORMAL' ? null : (
                <Etiqueta
                  texto={linha.destaque.replace(/_/g, ' ')}
                  tom={
                    linha.destaque === 'VARIACAO_ALTA'
                      ? 'critico'
                      : linha.destaque === 'REMOVIDO'
                        ? 'atencao'
                        : 'positivo'
                  }
                />
              )}
            </td>
          </tr>
        ))}
      </Tabela>
    </Cartao>
  );
}
